import type { FC, ReactNode } from "react";
import { lazy, useMemo } from "react";

import { CinLoaderFull } from "@cinnamon/design-system/blocks";
import { PopupProvider } from "@cinnamon/design-system/compounds";
import {
  type LinkProps,
  Navigate,
  Route,
  type RouteObject,
  RouterProvider,
  Routes,
  type To,
  createBrowserRouter,
  useLocation,
  useSearchParams,
} from "@cinnamon/design-system/hooks";
import compact from "lodash-es/compact";

import {
  AI_PIPELINE_TYPE,
  AUTO_CLASSIFICATION_CONFIG_TYPE,
  FIX_FORM_CONFIG_TYPE,
  LLM_CONFIG_TYPE,
  type LOGIN_METHOD,
  MANUAL_CLASSIFICATION_CONFIG_TYPE,
  SETTING_TYPE,
  SSOLoginType,
  WorkFlowType,
} from "@/constants/global";
import { LOGIN_SEARCH_PARAMS, ROUTE } from "@/constants/route";
import { getDemoRoutes } from "@/demo/route";
import { useIsEnableFeatureFlag } from "@/hooks";
import useGetLoginMethod from "@/hooks/useGetLoginMethod";
import useTenantAwareNavigationLinkProps from "@/hooks/useTenantAwareNavigationLinkProps";
import { LLM_SAMPLE_EDITOR_MODE } from "@/pages/LLMSampleEditor/constants";
import { useAppSelector } from "@/redux/hook";
import { systemSettingState, userState } from "@/redux/store";
import { FLAG_TICKET } from "@/types/featureFlag";

import AuthRoute from "./AuthRoute";
import { buildRedirectTo } from "./helpers";

const LoginPage = lazy(() => import("@/pages/Login/Login"));
const SSOLoginPage = lazy(() => import("@/pages/SSOLogin"));
const Verify2FaPage = lazy(() => import("@/pages/Verify2FA"));
const ChangePasswordPage = lazy(() => import("@/pages/ChangePassword"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPassword"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPassword"));
const NoPermissionPage = lazy(() => import("@/pages/NoPermission"));
const NotFoundPage = lazy(() => import("@/pages/NotFound"));

const WorkflowPage = lazy(() => import("@/pages/Workflow"));
const WorkflowUpsertPage = lazy(() => import("@/pages/Workflow/WorkflowUpsert"));
const WorkFlowDetailPage = lazy(() => import("@/pages/WorkflowDetail"));
const WorkFlowListPage = lazy(() => import("@/pages/Workflow/WorkFlowList"));

const AnalyticsGeneralPage = lazy(() => import("@/pages/AnalyticGeneral"));

const AiPipelineManagementPage = lazy(() => import("@/pages/AiPipelines"));

const FixFormConfigPage = lazy(() => import("@/pages/PipelineConfiguration/FixedFormConfig"));
const LLMConfigPage = lazy(() => import("@/pages/PipelineConfiguration/LLMConfig"));
const ManualClassificationConfigPage = lazy(() => import("@/pages/PipelineConfiguration/ManualClassification"));
const AutoClassificationConfigPage = lazy(() => import("@/pages/PipelineConfiguration/AutoClassification"));

const TemplateEditorPage = lazy(() => import("@/pages/TemplateEditor"));

const LLMSampleEditorPage = lazy(() => import("@/pages/LLMSampleEditor"));

const EditorPage = lazy(() => import("@/pages/Editor"));
const TeamPage = lazy(() => import("@/pages/Team"));
const UserPage = lazy(() => import("@/pages/Users"));
const SettingPage = lazy(() => import("@/pages/Setting"));
const PersonalSetting = lazy(() => import("@/pages/PersonalSetting"));

const AuthLayout = lazy(() => import("@/layouts/AuthLayout"));
const UnAuthLayout = lazy(() => import("@/layouts/UnAuthLayout"));

interface RouteType {
  name?: string;
  children?: ReactNode;
}

interface ConfigRoute {
  isAuth: boolean;
  flagsFeature: Partial<Record<FLAG_TICKET, boolean>>;
  loginMethod: LOGIN_METHOD | null;
}

interface UnAuthRoute extends Partial<ConfigRoute>, Pick<AuthRouteInterface, "getLinkProps"> {
  redirectTo: string | null;
}

const getUnAuthRoutes = ({ redirectTo, isAuth, getLinkProps }: UnAuthRoute): RouteObject => {
  const redirectToUrl = redirectTo ? getLinkProps(redirectTo).to : ROUTE.HOME.to;
  return {
    path: "*",
    element: !isAuth ? <UnAuthLayout /> : <Navigate replace to={redirectToUrl} />,
    children: [
      { path: ROUTE.LOGIN.path, element: <LoginPage /> },
      { path: ROUTE.SSO_LOGIN.path, element: <SSOLoginPage type={SSOLoginType.SSO_LOGIN} /> },
      { path: ROUTE.AUTH_CALLBACK.path, element: <SSOLoginPage type={SSOLoginType.AUTH_CALLBACK} /> },
    ],
  };
};

interface AuthRouteInterface extends Partial<ConfigRoute> {
  prevPathName: string;
  prevSearch: string;
  getLinkProps: (to: To) => LinkProps;
}

type AuthRouteParams = Pick<AuthRouteInterface, "getLinkProps">;

// Helper function to create workflow routes
const getWorkflowRoutes = ({ getLinkProps }: AuthRouteParams): Array<RouteObject> => [
  {
    path: "*",
    element: <Navigate replace to={getLinkProps(ROUTE.NOT_FOUND.to).to} />,
  },
  {
    path: "",
    element: <Navigate replace to={getLinkProps(ROUTE.WORKFLOW.to).to} />,
  },
  {
    path: ROUTE.WORKFLOW.path,
    element: <WorkflowPage />,
    children: [
      {
        path: ":workflowId",
        element: <AuthRoute element={<WorkFlowDetailPage />} permissions={ROUTE.WORKFLOW.permissions} />,
      },
      {
        path: ROUTE.WORKFLOW_NEW.to,
        element: (
          <AuthRoute
            element={<WorkflowUpsertPage type={WorkFlowType.OCR} />}
            permissions={ROUTE.WORKFLOW_NEW.permissions}
          />
        ),
      },
      {
        path: ROUTE.WORKFLOW_NEW_CLASSIFICATION.to,
        element: (
          <AuthRoute
            element={<WorkflowUpsertPage type={WorkFlowType.CLASSIFICATION} />}
            permissions={ROUTE.WORKFLOW_NEW_CLASSIFICATION.permissions}
          />
        ),
      },
      {
        path: ROUTE.WORKFLOW_EDIT.path,
        element: <AuthRoute element={<WorkflowUpsertPage />} permissions={ROUTE.WORKFLOW_EDIT.permissions} />,
      },
      {
        path: ROUTE.WORKFLOW_LIST.to,
        element: <AuthRoute element={<WorkFlowListPage />} permissions={ROUTE.WORKFLOW_LIST.permissions} />,
      },
    ],
  },
];

// Helper function to create basic routes
const getBasicRoutes = (
  ssoEnabled?: boolean
): Array<{
  path: string;
  element: JSX.Element;
}> => {
  const routes = [
    {
      path: ROUTE.EDITOR.path,
      element: <AuthRoute element={<EditorPage />} permissions={ROUTE.EDITOR.permissions} />,
    },
    {
      path: ROUTE.PERSONAL_SETTING.path,
      element: <AuthRoute element={<PersonalSetting />} permissions={ROUTE.PERSONAL_SETTING.permissions} />,
    },
  ];
  if (!ssoEnabled) {
    routes.push({
      path: ROUTE.CHANGE_PASSWORD.path,
      element: <AuthRoute element={<ChangePasswordPage />} permissions={ROUTE.CHANGE_PASSWORD.permissions} />,
    });
  }
  return routes;
};

// Helper function to create AI pipeline routes
const getAiPipelineRoutes = ({ getLinkProps }: AuthRouteParams): Array<RouteObject> => [
  {
    path: ROUTE.AI_PIPELINE.path,
    element: <Navigate replace to={getLinkProps(ROUTE.AI_PIPELINE_DATA_EXTRACTION.to).to} />,
  },
  {
    path: ROUTE.AI_PIPELINE_DATA_EXTRACTION.path,
    element: (
      <AuthRoute
        element={<AiPipelineManagementPage type={AI_PIPELINE_TYPE.DATA_EXTRACTION} />}
        permissions={ROUTE.AI_PIPELINE_DATA_EXTRACTION.permissions}
      />
    ),
  },
  {
    path: ROUTE.AI_PIPELINE_CLASSIFICATION.path,
    element: (
      <AuthRoute
        element={<AiPipelineManagementPage type={AI_PIPELINE_TYPE.CLASSIFICATION} />}
        permissions={ROUTE.AI_PIPELINE_CLASSIFICATION.permissions}
      />
    ),
  },
];

// Helper function to create fix form config routes
const getFixFormConfigRoutes = (): Array<{
  path: string;
  element: JSX.Element;
}> => [
  {
    path: ROUTE.FIX_FORM_CONFIG_FIELDS.path,
    element: (
      <AuthRoute
        element={<FixFormConfigPage type={FIX_FORM_CONFIG_TYPE.FIELDS} />}
        permissions={ROUTE.FIX_FORM_CONFIG_FIELDS.permissions}
      />
    ),
  },
  {
    path: ROUTE.FIX_FORM_CONFIG_TEMPLATES.path,
    element: (
      <AuthRoute
        element={<FixFormConfigPage type={FIX_FORM_CONFIG_TYPE.TEMPLATES} />}
        permissions={ROUTE.FIX_FORM_CONFIG_TEMPLATES.permissions}
      />
    ),
  },
  {
    path: ROUTE.FIX_FORM_CONFIG_DATA_ENHANCEMENT.path,
    element: (
      <AuthRoute
        element={<FixFormConfigPage type={FIX_FORM_CONFIG_TYPE.DATA_ENHANCEMENT} />}
        permissions={ROUTE.FIX_FORM_CONFIG_DATA_ENHANCEMENT.permissions}
      />
    ),
  },
  {
    path: ROUTE.FIX_FORM_CONFIG_RELEASES.path,
    element: (
      <AuthRoute
        element={<FixFormConfigPage type={FIX_FORM_CONFIG_TYPE.RELEASES} />}
        permissions={ROUTE.FIX_FORM_CONFIG_RELEASES.permissions}
      />
    ),
  },
];

// Helper function to create LLM config routes
const getLLMConfigRoutes = (): Array<{
  path: string;
  element: JSX.Element;
}> => [
  {
    path: ROUTE.LLM_CONFIG_AI_SETTING_MODEL.path,
    element: (
      <AuthRoute
        element={<LLMConfigPage type={LLM_CONFIG_TYPE.AI_SETTING_MODEL} />}
        permissions={ROUTE.LLM_CONFIG_AI_SETTING_MODEL.permissions}
      />
    ),
  },
  {
    path: ROUTE.LLM_CONFIG_SINGLE_FIELDS.path,
    element: (
      <AuthRoute
        element={<LLMConfigPage type={LLM_CONFIG_TYPE.SINGLE_FIELDS} />}
        permissions={ROUTE.LLM_CONFIG_SINGLE_FIELDS.permissions}
      />
    ),
  },
  {
    path: ROUTE.LLM_CONFIG_TABLE_FIELDS.path,
    element: (
      <AuthRoute
        element={<LLMConfigPage type={LLM_CONFIG_TYPE.TABLE_FIELDS} />}
        permissions={ROUTE.LLM_CONFIG_TABLE_FIELDS.permissions}
      />
    ),
  },
  {
    path: ROUTE.LLM_CONFIG_SAMPLES.path,
    element: (
      <AuthRoute
        element={<LLMConfigPage type={LLM_CONFIG_TYPE.SAMPLES} />}
        permissions={ROUTE.LLM_CONFIG_SAMPLES.permissions}
      />
    ),
  },
  {
    path: ROUTE.LLM_CONFIG_DATA_ENHANCEMENT.path,
    element: (
      <AuthRoute
        element={<LLMConfigPage type={LLM_CONFIG_TYPE.DATA_ENHANCEMENT} />}
        permissions={ROUTE.LLM_CONFIG_DATA_ENHANCEMENT.permissions}
      />
    ),
  },
  {
    path: ROUTE.LLM_CONFIG_RELEASES.path,
    element: (
      <AuthRoute
        element={<LLMConfigPage type={LLM_CONFIG_TYPE.RELEASES} />}
        permissions={ROUTE.LLM_CONFIG_RELEASES.permissions}
      />
    ),
  },
];

// Helper function to create LLM sample editor routes
const getLLMSampleEditorRoutes = (): Array<{
  path: string;
  element: JSX.Element;
}> => [
  {
    path: ROUTE.LLM_SAMPLE_EDITOR_CREATE.path,
    element: (
      <AuthRoute
        element={<LLMSampleEditorPage mode={LLM_SAMPLE_EDITOR_MODE.CREATE_NEW} />}
        permissions={ROUTE.LLM_SAMPLE_EDITOR.permissions}
      />
    ),
  },
  {
    path: ROUTE.LLM_SAMPLE_EDITOR_CREATE_FROM_JOB.path,
    element: <AuthRoute element={<LLMSampleEditorPage mode={LLM_SAMPLE_EDITOR_MODE.CREATE_FROM_JOB} />} />,
  },
  {
    path: ROUTE.LLM_SAMPLE_EDITOR.path,
    element: <AuthRoute element={<LLMSampleEditorPage />} permissions={ROUTE.LLM_SAMPLE_EDITOR.permissions} />,
  },
];

// Helper function to create access permission routes
const getAccessPermissionRoutes = ({
  getLinkProps,
}: AuthRouteParams): Array<{
  path: string;
  element: JSX.Element;
}> => [
  {
    path: ROUTE.ACCESS_PERMISSION.path,
    element: <Navigate replace to={getLinkProps(ROUTE.ACCESS_PERMISSION_USERS.to).to} />,
  },
  {
    path: ROUTE.ACCESS_PERMISSION_USERS.path,
    element: <AuthRoute element={<UserPage />} permissions={ROUTE.ACCESS_PERMISSION_USERS.permissions} />,
  },
  {
    path: ROUTE.ACCESS_PERMISSION_TEAMS.path,
    element: <AuthRoute element={<TeamPage />} permissions={ROUTE.ACCESS_PERMISSION_TEAMS.permissions} />,
  },
];

// Helper function to create manual classification routes
const getManualClassificationRoutes = (): Array<{
  path: string;
  element: JSX.Element;
}> => [
  {
    path: ROUTE.MANUAL_CLASSIFICATION_CLASS_CONFIG.path,
    element: (
      <AuthRoute
        element={<ManualClassificationConfigPage type={MANUAL_CLASSIFICATION_CONFIG_TYPE.CLASS} />}
        permissions={ROUTE.MANUAL_CLASSIFICATION_CLASS_CONFIG.permissions}
      />
    ),
  },
  {
    path: ROUTE.MANUAL_CLASSIFICATION_RELEASES.path,
    element: (
      <AuthRoute
        element={<ManualClassificationConfigPage type={MANUAL_CLASSIFICATION_CONFIG_TYPE.RELEASES} />}
        permissions={ROUTE.MANUAL_CLASSIFICATION_RELEASES.permissions}
      />
    ),
  },
];

const getAutoClassificationRoutes = (): Array<{
  path: string;
  element: JSX.Element;
}> => [
  {
    path: ROUTE.AUTO_CLASSIFICATION_CLASS_CONFIG.path,
    element: (
      <AuthRoute
        element={<AutoClassificationConfigPage type={AUTO_CLASSIFICATION_CONFIG_TYPE.CLASS} />}
        permissions={ROUTE.AUTO_CLASSIFICATION_CLASS_CONFIG.permissions}
      />
    ),
  },
  {
    path: ROUTE.AUTO_CLASSIFICATION_RELEASES.path,
    element: (
      <AuthRoute
        element={<AutoClassificationConfigPage type={AUTO_CLASSIFICATION_CONFIG_TYPE.RELEASES} />}
        permissions={ROUTE.AUTO_CLASSIFICATION_RELEASES.permissions}
      />
    ),
  },
];

// Helper function to create setting ip restriction routes
const getSettingIPRestrictionRoutes = (): Array<{
  path: string;
  element: JSX.Element;
}> => [
  {
    path: ROUTE.SETTING_IP_RESTRICTION.path,
    element: (
      <AuthRoute
        element={<SettingPage settingType={SETTING_TYPE.IP_RESTRICTION} />}
        permissions={ROUTE.SETTING_IP_RESTRICTION.permissions}
      />
    ),
  },
];

const getSetting2FaRoutes = (
  ssoEnabled?: boolean
): Array<{
  path: string;
  element: JSX.Element;
}> => {
  if (ssoEnabled) {
    return [];
  }
  return [
    {
      path: ROUTE.SETTING_2FA.path,
      element: (
        <AuthRoute
          element={<SettingPage settingType={SETTING_TYPE.TWO_FACTOR_AUTHENTICATION} />}
          permissions={ROUTE.SETTING_2FA.permissions}
        />
      ),
    },
  ];
};

// Helper function to create setting routes
const getSettingRoutes = ({ getLinkProps }: AuthRouteParams): Array<RouteObject> => [
  {
    path: ROUTE.SETTING.path,
    element: <Navigate replace to={getLinkProps(ROUTE.SETTING_GENERAL.to).to} />,
  },
  {
    path: ROUTE.SETTING_GENERAL.path,
    element: (
      <AuthRoute element={<SettingPage settingType={SETTING_TYPE.GENERAL} />} permissions={ROUTE.SETTING.permissions} />
    ),
  },
  {
    path: ROUTE.SETTING_API_KEY.path,
    element: (
      <AuthRoute
        element={<SettingPage settingType={SETTING_TYPE.API_KEY_MANAGEMENT} />}
        permissions={ROUTE.SETTING_API_KEY.permissions}
      />
    ),
  },
  {
    path: ROUTE.SETTING_LICENSE.path,
    element: (
      <AuthRoute
        element={<SettingPage settingType={SETTING_TYPE.LICENSE_MANAGEMENT} />}
        permissions={ROUTE.SETTING_LICENSE.permissions}
      />
    ),
  },
  {
    path: ROUTE.SETTING_FILE_RETENTION_POLICY.path,
    element: (
      <AuthRoute
        element={<SettingPage settingType={SETTING_TYPE.FILE_RETENTION_POLICY} />}
        permissions={ROUTE.SETTING_FILE_RETENTION_POLICY.permissions}
      />
    ),
  },
];

// Helper function to create analytics routes
const getAnalyticsRoutes = ({ getLinkProps }: AuthRouteParams): Array<RouteObject> => [
  {
    path: ROUTE.ANALYTICS.path,
    element: <Navigate replace to={getLinkProps(ROUTE.ANALYTICS_GENERAL.to).to} />,
  },
  {
    path: ROUTE.ANALYTICS_GENERAL.path,
    element: <AuthRoute element={<AnalyticsGeneralPage />} permissions={ROUTE.ANALYTICS_GENERAL.permissions} />,
  },
];

const getAuthRoutes = (
  {
    isAuth,
    prevPathName,
    prevSearch,
    loginMethod,
    ssoEnabled,
    getLinkProps,
  }: AuthRouteInterface & { ssoEnabled?: boolean; getLinkProps: (to: To) => LinkProps },
  flagFeature?: Partial<Record<FLAG_TICKET, boolean>>
): RouteObject => {
  const unauthenticatedRedirectTo = buildRedirectTo(prevPathName, prevSearch, loginMethod ?? null);

  return {
    path: ROUTE.HOME.to,
    element: isAuth ? <AuthLayout /> : <Navigate to={unauthenticatedRedirectTo} />,
    children: compact<RouteObject>([
      ...getWorkflowRoutes({ getLinkProps }),
      ...getBasicRoutes(ssoEnabled),
      ...getAiPipelineRoutes({ getLinkProps }),
      ...getFixFormConfigRoutes(),
      ...getLLMConfigRoutes(),
      ...getLLMSampleEditorRoutes(),
      {
        path: ROUTE.TEMPLATE_EDITOR.path,
        element: <AuthRoute element={<TemplateEditorPage />} permissions={ROUTE.TEMPLATE_EDITOR.permissions} />,
      },
      ...getAccessPermissionRoutes({ getLinkProps }),
      ...getSettingRoutes({ getLinkProps }),
      ...getAnalyticsRoutes({ getLinkProps }),
      ...getManualClassificationRoutes(),
      ...(flagFeature?.[FLAG_TICKET["FSH2410-3523"]] ? getAutoClassificationRoutes() : []),
      ...getSettingIPRestrictionRoutes(),
      ...getSetting2FaRoutes(ssoEnabled),
    ]),
  };
};

const getOtherRoutes = ({
  isAuth,
  getLinkProps,
}: Partial<ConfigRoute> & AuthRouteParams): Array<{
  path: string;
  element: JSX.Element;
}> => [
  { path: ROUTE.NO_PERMISSION.path, element: <NoPermissionPage /> },
  { path: ROUTE.NOT_FOUND.path, element: <NotFoundPage /> },
  { path: ROUTE.FORGOT_PASSWORD.path, element: <ForgotPasswordPage /> },
  { path: ROUTE.RESET_PASSWORD.path, element: <ResetPasswordPage /> },
  {
    path: ROUTE.VERIFY_2FA.path,
    element: !isAuth ? <Verify2FaPage /> : <Navigate replace to={getLinkProps(ROUTE.HOME.to).to} />,
  },
];

const Root: FC = (): JSX.Element => {
  const { user, isInitialized, isAuthenticated } = useAppSelector(userState);
  const systemSetting = useAppSelector(systemSettingState);
  const { pathname, search } = useLocation();
  const [searchParams] = useSearchParams();
  const redirectTo = useMemo(() => searchParams.get(LOGIN_SEARCH_PARAMS.REDIRECT_TO), [searchParams]);
  const isAuth = isAuthenticated && user;
  const flagAutoClassification = useIsEnableFeatureFlag(FLAG_TICKET["FSH2410-3523"]);

  const { loginMethod, isLoading: isLoadingLoginMethod } = useGetLoginMethod({
    skip: !isInitialized || !!isAuth || pathname === ROUTE.SSO_LOGIN.to,
  });

  const getLinkProps = useTenantAwareNavigationLinkProps();

  const unAuthRoutes = useMemo(
    () => getUnAuthRoutes({ redirectTo, isAuth: !!isAuth, getLinkProps }),
    [isAuth, redirectTo, getLinkProps]
  );
  const authRoutes = useMemo(
    () =>
      getAuthRoutes(
        {
          isAuth: !!isAuth,
          prevPathName: pathname,
          prevSearch: search,
          loginMethod,
          ssoEnabled: systemSetting.ssoEnabled,
          getLinkProps,
        },
        {
          [FLAG_TICKET["FSH2410-3523"]]: flagAutoClassification,
        }
      ),
    [isAuth, pathname, search, loginMethod, systemSetting.ssoEnabled, getLinkProps, flagAutoClassification]
  );
  const otherRoutes = getOtherRoutes({
    isAuth: !!isAuth,
    getLinkProps,
  });
  const demoRoutes = getDemoRoutes();

  if (!isInitialized || isLoadingLoginMethod) {
    return <CinLoaderFull className="flex-1" />;
  }

  return (
    <Routes>
      {/* unAuthRoute */}
      <Route element={unAuthRoutes.element} path={unAuthRoutes.path}>
        {unAuthRoutes.children?.map((route) => (
          <Route key={`${unAuthRoutes.path}-${route.path}`} element={route.element} path={route.path} />
        ))}
      </Route>

      {/* authRoute */}
      <Route element={authRoutes.element} path={authRoutes.path}>
        {authRoutes.children?.map((route) => (
          <Route key={`${authRoutes.path}-${route.path}`} element={route.element} path={route.path}>
            {route.children?.map((child) => (
              <Route key={`${authRoutes.path}-${route.path}-${child.path}`} element={child.element} path={child.path} />
            ))}
          </Route>
        ))}

        {/* Demo Route */}
        <Route path={ROUTE.DEMO.path}>
          {demoRoutes.map((route) => (
            <Route
              key={`${ROUTE.DEMO.path}-${route.path}`}
              element={route.element}
              path={`/${ROUTE.DEMO.path}/${route.path}`}
            />
          ))}
        </Route>
      </Route>

      {/* other Auth */}
      {otherRoutes.map((route) => (
        <Route key={route.path} element={route.element} path={route.path} />
      ))}
    </Routes>
  );
};

const RouteConfig: FC<RouteType> = (): JSX.Element => {
  const router = createBrowserRouter([
    {
      path: "*",
      element: (
        <PopupProvider>
          <Root />
        </PopupProvider>
      ),
    },
  ]);

  return <RouterProvider router={router} />;
};

export default RouteConfig;
